import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import injectSheet from 'react-jss';

import { Button } from '~/eds/src/components/Button/Button';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { AddAlertPanel } from '~/features/alerts/AddAlertPanel';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';

import { MODAL_ALERT, MODAL_DELETE } from '../../../types/modal.types';
import { ERROR, SUCCESS } from '../../../types/toast.types';
import ActionsMenu from '../../Shared/ActionsMenu/ActionsMenu';
import EcCard from '../../Shared/EcCard/EcCard';
import EcCheckbox from '../../Shared/EcCheckbox/EcCheckbox';
import EcModal from '../../Shared/EcModal/EcModal';
import EcPaginate from '../../Shared/EcPaginate/EcPaginate';
import { showToast } from '../../Shared/EcToast/showToast';
import LoadingSpinner from '../../Shared/Icons/LoadingSpinner';
import SelectDeselectAllMenu from '../../Shared/SelectDeselectAllMenu/SelectDeselectAllMenu';
import {
  deleteAlerts,
  sendAlertEmail,
  toggleMuteAlert,
  updateAlert,
} from '../AlertsPage.data';
import styles from './AlertsList.styles';

const ecCardContentStyles = { padding: '0 16px' };

const MODAL_SIMPLE_SEND_EMAIL = 'MODAL_SIMPLE_SEND_EMAIL';
const MODAL_SIMPLE_MUTE = 'MODAL_SIMPLE_MUTE';
const MODAL_SIMPLE_ACTIVATE = 'MODAL_SIMPLE_ACTIVATE';

class AlertsList extends Component {
  constructor(props) {
    super(props);

    this.handleHideModal = this.handleHideModal.bind(this);
    this.state = {
      allSelected: false,
      selectedItemsList: [],
      pageSize: 20,
      primaryModal: null,
    };
  }

  resetSelection() {
    this.setState({ allSelected: false, selectedItemsList: [] });
  }

  handleOnSelectClick(isSelected) {
    const { alerts } = this.props;

    let newSelectedItems = [];
    if (isSelected) alerts.forEach((item) => newSelectedItems.push(item.id));

    this.setState({
      allSelected: isSelected,
      selectedItemsList: newSelectedItems,
    });
  }

  handleOnSelectItemClick(itemId) {
    const { selectedItemsList } = this.state;

    const selectedItemIndex = selectedItemsList.indexOf(itemId);

    let newSelectedItems = [...selectedItemsList];
    if (selectedItemIndex > -1) {
      newSelectedItems.splice(selectedItemIndex, 1);
    } else {
      newSelectedItems.push(itemId);
    }

    this.setState({ selectedItemsList: newSelectedItems });
  }

  handleHideModal() {
    this.setState({ primaryModal: null });
  }

  handleToggleMuteClick(alert, actionText) {
    const { handleUpdateItem, intl } = this.props;

    toggleMuteAlert(alert.id, !alert.muted).then(() =>
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'AlertsList.alertActionSuccess',
            defaultMessage: 'This alert has been successfully: {action}',
            description:
              'Success message for alert actions. Example: action = "activated"',
          },
          { action: actionText.toLocaleLowerCase() },
        ),
      ),
    );
    handleUpdateItem({ ...alert, muted: !alert.muted });
    this.handleHideModal();
  }

  handleActivateClick(alert, actionText) {
    const { handleUpdateItem, intl } = this.props;

    updateAlert(alert.id, { active: !alert.active }).then((ret) => {
      handleUpdateItem({ ...ret, active: !alert.active });
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'AlertsList.alertActionSuccess',
            defaultMessage: 'This alert has been successfully: {action}',
            description:
              'Success message for alert actions. Example: action = "activated"',
          },
          { action: actionText.toLocaleLowerCase() },
        ),
      );
    });
    this.handleHideModal();
  }

  renderAlertModal() {
    const { onItemUpdate, onItemCreate, flags } = this.props;
    const { primaryModal } = this.state;
    const { alert, step, isDuplicated } = primaryModal;

    const enableModalConsolidation = flags[FlagType.ModalConsolidation];

    return enableModalConsolidation ? (
      <AddAlertPanel
        width="1080px"
        hideModal={this.handleHideModal}
        onItemCreate={onItemCreate}
        onItemUpdate={onItemUpdate}
        isDuplicated={isDuplicated}
        step={step}
        alertId={alert.id}
      />
    ) : (
      <EcModal
        modalType={MODAL_ALERT}
        width="1080px"
        alertId={alert.id}
        step={step}
        isDuplicated={isDuplicated}
        handleAlertAction={isDuplicated ? onItemCreate : onItemUpdate}
        hideModal={this.handleHideModal}
      />
    );
  }

  renderActivateModal() {
    const { primaryModal } = this.state;
    const { intl } = this.props;

    const { alert } = primaryModal;
    const actionText = alert.active
      ? intl.formatMessage({
          id: 'AlertsList.deactivateAction',
          defaultMessage: 'Deactivate',
          description: 'Action text for deactivating an alert',
        })
      : intl.formatMessage({
          id: 'AlertsList.activateAction',
          defaultMessage: 'Activate',
          description: 'Action text for activating an alert',
        });

    return (
      <Modal
        children={
          <Layout>
            <FormattedMessage
              id="AlertsList.confirmActivateDeactivate"
              defaultMessage='Are you sure you want to {action} the "{name}" alert?'
              description="Confirmation message for activating/deactivating an alert. Example: action = 'activate', name = 'Contract Expiry Alert'"
              values={{
                action: actionText.toLocaleLowerCase(),
                name: <strong>{alert.name}</strong>,
              }}
            />
          </Layout>
        }
        isVisible={primaryModal && primaryModal.type === MODAL_SIMPLE_ACTIVATE}
        onHide={this.handleHideModal}
        onCancel={this.handleHideModal}
        primaryAction={{
          text: actionText,
          variant: 'primary',
          onClick: () => {
            this.handleActivateClick(alert, actionText);
            this.handleHideModal();
          },
        }}
        title={intl.formatMessage(
          {
            id: 'AlertsList.activateDeactivateTitle',
            defaultMessage: 'Perform the following action: {action}',
            description:
              'Modal title for activating/deactivating an alert. Example: action = "activate"',
          },
          { action: actionText },
        )}
      />
    );
  }

  renderMuteAlertModal() {
    const { primaryModal } = this.state;
    const { intl } = this.props;

    const { alert } = primaryModal;

    const actionText = alert.muted
      ? intl.formatMessage({
          id: 'AlertsList.unmuteAction',
          defaultMessage: 'Unmute',
          description: 'Action text for unmuting an alert',
        })
      : intl.formatMessage({
          id: 'AlertsList.muteAction',
          defaultMessage: 'Mute',
          description: 'Action text for muting an alert',
        });

    const modalTitle = intl.formatMessage(
      {
        id: 'AlertsList.muteUnmuteTitle',
        defaultMessage: 'Perform the following action: {action}',
        description:
          'Modal title for muting/unmuting an alert. Example: action = "mute"',
      },
      { action: actionText },
    );

    return (
      <Modal
        children={
          <Layout>
            <Text>
              {alert.muted ? (
                <FormattedMessage
                  id="AlertsList.confirmUnmute"
                  defaultMessage="Are you sure you want to unmute the {name} alert? Going forward, you will receive emails regarding this alert."
                  description="Confirmation message for unmuting an alert. Example: action = 'unmute', name = 'Contract Expiry Alert'"
                  values={{
                    name: alert.name,
                  }}
                />
              ) : (
                <FormattedMessage
                  id="AlertsList.confirmMute"
                  defaultMessage="Are you sure you want to mute the {name} alert? Going forward, you won't receive any emails regarding this alert."
                  description="Confirmation message for muting an alert. Example: action = 'mute', name = 'Contract Expiry Alert'"
                  values={{
                    name: <strong>{alert.name}</strong>,
                  }}
                />
              )}
            </Text>
          </Layout>
        }
        isVisible={primaryModal && primaryModal.type === MODAL_SIMPLE_MUTE}
        onHide={this.handleHideModal}
        onCancel={this.handleHideModal}
        title={modalTitle}
        primaryAction={{
          text: actionText,
          variant: 'primary',
          onClick: () => this.handleToggleMuteClick(alert, actionText),
        }}
      />
    );
  }

  handleSendEmail(alertId, sendToAll) {
    const { intl } = this.props;
    const toastMessage = sendToAll
      ? intl.formatMessage({
          id: 'AlertsList.emailSentToAll',
          defaultMessage:
            'The email has been sent to all the recipients of the alert.',
          description: 'Success message when email is sent to all recipients',
        })
      : intl.formatMessage({
          id: 'AlertsList.emailSent',
          defaultMessage: 'The email has been sent.',
          description: 'Success message when email is sent',
        });

    sendAlertEmail(alertId, sendToAll)
      .then(() => showToast(SUCCESS, toastMessage))
      .catch(() =>
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'AlertsList.emailSendError',
            defaultMessage: 'Something went wrong with sending an email.',
            description: 'Error message when email sending fails',
          }),
        ),
      );
  }

  renderSendEmailModal() {
    const { primaryModal } = this.state;
    const { intl } = this.props;

    const { alert } = primaryModal;
    const id = alert.id;

    return (
      <Modal
        title={intl.formatMessage({
          id: 'AlertsList.sendEmailTitle',
          defaultMessage: 'Send Email',
          description: 'Modal title for sending email',
        })}
        children={
          <Text>
            {intl.formatMessage({
              id: 'AlertsList.sendEmailDescription',
              defaultMessage:
                'In order to review the alert, you can trigger it immediately. This will not affect the regular schedule and frequency.',
              description: 'Description for sending email modal',
            })}
          </Text>
        }
        isVisible={
          primaryModal && primaryModal.type === MODAL_SIMPLE_SEND_EMAIL
        }
        onHide={this.handleHideModal}
        onCancel={this.handleHideModal}
        primaryAction={{
          text: intl.formatMessage({
            id: 'AlertsList.emailOnlyMe',
            defaultMessage: 'Email Only Me',
            description:
              'Button text to email only the current user | maxLength=20',
          }),
          variant: 'primary',
          onClick: () => {
            this.handleSendEmail(id, false);
            this.handleHideModal();
          },
        }}
        secondaryActions={[
          {
            text: intl.formatMessage({
              id: 'AlertsList.emailAllRecipients',
              defaultMessage: 'Email All Recipients',
              description: 'Button text to email all recipients | maxLength=20',
            }),
            onClick: () => {
              this.handleSendEmail(id, true);
              this.handleHideModal();
            },
          },
        ]}
      />
    );
  }

  renderDeleteModal() {
    const { selectedItemsList, primaryModal } = this.state;
    const { handleDeleteItems, intl } = this.props;

    const { alert } = primaryModal;

    const itemsToDelete = selectedItemsList.length
      ? selectedItemsList
      : [alert];
    const modalTitle = selectedItemsList.length
      ? intl.formatMessage({
          id: 'AlertsList.deleteItemsTitle',
          defaultMessage: 'Delete Items?',
          description: 'Modal title for deleting multiple items',
        })
      : intl.formatMessage({
          id: 'AlertsList.deleteItemTitle',
          defaultMessage: 'Delete Item?',
          description: 'Modal title for deleting single item',
        });
    const messageSubjectText = selectedItemsList.length
      ? intl.formatMessage(
          {
            id: 'AlertsList.selectedItemsText',
            defaultMessage: '{count} items you have selected?',
            description:
              'Text describing selected items for deletion. Example: count = "3"',
          },
          { count: itemsToDelete.length },
        )
      : intl.formatMessage(
          {
            id: 'AlertsList.alertNameText',
            defaultMessage: 'alert named: {name}?',
            description:
              'Text describing alert name for deletion. Example: name = "Contract Expiry Alert"',
          },
          { name: alert.name },
        );

    return (
      <Modal
        children={
          <Markdown
            text={intl.formatMessage(
              {
                id: 'AlertsList.confirmDelete',
                defaultMessage:
                  "Are you sure you want to delete the {subject} You won't be able to undo this action.",
                description:
                  'Confirmation message for deleting alerts. Example: subject = "Contract Expiry Alert"',
              },
              { subject: messageSubjectText },
            )}
          />
        }
        isVisible={true}
        onHide={this.handleHideModal}
        onCancel={this.handleHideModal}
        primaryAction={{
          text: intl.formatMessage({
            id: 'AlertsList.deleteButton',
            defaultMessage: 'Delete',
            description: 'Button text for delete action',
          }),
          variant: 'danger',
          onClick: () => {
            const itemsToDelete = selectedItemsList.length
              ? selectedItemsList
              : [alert.id];
            deleteAlerts(itemsToDelete)
              .then(() => {
                showToast(
                  SUCCESS,
                  intl.formatMessage({
                    id: 'AlertsList.deleteSuccess',
                    defaultMessage: 'The alert has been successfully deleted.',
                    description: 'Success message for alert deletion',
                  }),
                );
                handleDeleteItems(itemsToDelete);
              })
              .catch(() => {
                showToast(
                  ERROR,
                  intl.formatMessage({
                    id: 'AlertsList.deleteError',
                    defaultMessage:
                      'Something went wrong with deleting this alert.',
                    description: 'Error message for alert deletion failure',
                  }),
                );
              });
            this.resetSelection();
            this.handleHideModal();
          },
        }}
        title={modalTitle}
      />
    );
  }

  getMenuItems = (alert) => {
    const { intl } = this.props;
    const items = [];

    if (alert.is_editable) {
      items.push(
        {
          content: intl.formatMessage({
            id: 'AlertsList.editInfoAndSchedule',
            defaultMessage: 'Edit Info and Schedule',
            description: 'Menu item for editing alert info and schedule',
          }),
          id: `edit_info_and_schedule_for_alert?id=${alert.id}`,
          onClick: () =>
            this.setState({
              primaryModal: { type: MODAL_ALERT, alert, step: 1 },
            }),
        },
        {
          content: intl.formatMessage({
            id: 'AlertsList.editFilters',
            defaultMessage: 'Edit Filters',
            description: 'Menu item for editing alert filters',
          }),
          id: `edit_filters_for_alert?id=${alert.id}`,
          onClick: () =>
            this.setState({
              primaryModal: { type: MODAL_ALERT, alert, step: 2 },
            }),
        },
        {
          content: intl.formatMessage({
            id: 'AlertsList.editRecipients',
            defaultMessage: 'Edit Recipients',
            description: 'Menu item for editing alert recipients',
          }),
          id: `edit_recipients_for_alert?id=${alert.id}`,
          onClick: () =>
            this.setState({
              primaryModal: { type: MODAL_ALERT, alert, step: 3 },
            }),
        },
      );
    }

    items.push(
      {
        content: intl.formatMessage({
          id: 'AlertsList.duplicateAction',
          defaultMessage: 'Duplicate',
          description: 'Menu item for duplicating an alert',
        }),
        onClick: () =>
          this.setState({
            primaryModal: {
              type: MODAL_ALERT,
              alert,
              step: 1,
              isDuplicated: true,
            },
          }),
      },
      {
        content: `${
          alert.muted
            ? intl.formatMessage({
                id: 'AlertsList.unmuteAction',
                defaultMessage: 'Unmute',
                description: 'Action text for unmuting an alert',
              })
            : intl.formatMessage({
                id: 'AlertsList.muteAction',
                defaultMessage: 'Mute',
                description: 'Action text for muting an alert',
              })
        }`,
        onClick: () =>
          this.setState({ primaryModal: { type: MODAL_SIMPLE_MUTE, alert } }),
      },
      {
        content: alert.active
          ? intl.formatMessage({
              id: 'AlertsList.deactivateAction',
              defaultMessage: 'Deactivate',
              description: 'Action text for deactivating an alert',
            })
          : intl.formatMessage({
              id: 'AlertsList.activateAction',
              defaultMessage: 'Activate',
              description: 'Action text for activating an alert',
            }),
        onClick: () =>
          this.setState({
            primaryModal: { type: MODAL_SIMPLE_ACTIVATE, alert },
          }),
      },
      {
        content: intl.formatMessage({
          id: 'AlertsList.sendEmailNow',
          defaultMessage: 'Send Email Now',
          description: 'Menu item for sending email immediately',
        }),
        onClick: () =>
          this.setState({
            primaryModal: { type: MODAL_SIMPLE_SEND_EMAIL, alert },
          }),
      },
      {
        content: intl.formatMessage({
          id: 'AlertsList.deleteAction',
          defaultMessage: 'Delete',
          description: 'Menu item for deleting an alert',
        }),
        onClick: () =>
          this.setState({ primaryModal: { type: MODAL_DELETE, alert } }),
      },
    );

    return items;
  };

  renderAlertsItems() {
    const { classes, alerts, formatDate, intl } = this.props;
    const { selectedItemsList } = this.state;

    return alerts.map((alert) => {
      const {
        id,
        name,
        recipients,
        created_by,
        last_alert_date,
        upcoming_alert_date,
        active,
        muted,
      } = alert;

      const lastAlert = formatDate(last_alert_date, 'short_datetime');
      const upcomingAlert = formatDate(upcoming_alert_date, 'short_datetime');

      return (
        <tr key={id}>
          <td>
            <EcCheckbox
              id={`select-alert-id=${id}-checkbox`}
              checked={selectedItemsList.includes(id)}
              onClick={() => this.handleOnSelectItemClick(id)}
            />
            <label
              className="screenReaderText"
              htmlFor={`select-alert-id=${id}-checkbox`}
            >
              {intl.formatMessage(
                {
                  id: 'AlertsList.selectAlertLabel',
                  defaultMessage: 'Select alert named: {name} ',
                  description:
                    'Screen reader label for selecting individual alert. Example: name = "Contract Expiry Alert"',
                },
                { name },
              )}
            </label>
          </td>
          <td className={classes.cellName}>
            <div className={classes.alertNameText} title={name}>
              {name}
            </div>
          </td>
          <td className={classes.cellRecipients}>
            {intl.formatMessage(
              {
                id: 'AlertsList.recipientsCount',
                defaultMessage:
                  '{count} {count, plural, one {recipient} other {recipients}}',
                description: 'Count of alert recipients with pluralization',
              },
              { count: recipients },
            )}
          </td>
          <td className={classes.cellCreatedBy}>
            <div className={classes.createdByText} title={created_by}>
              {created_by}
            </div>
          </td>
          <td>
            <div className={classes.dateCellWrapper}>{lastAlert}</div>
          </td>
          <td>
            <div className={classes.dateCellWrapper}>{upcomingAlert}</div>
          </td>
          <td className={classes.cellActive}>
            {active ? (
              <Icon
                color="status.success"
                icon="check"
                tooltip={intl.formatMessage({
                  id: 'AlertsList.activeAlertTooltip',
                  defaultMessage: 'Active alert',
                  description: 'Tooltip for active alert icon',
                })}
              />
            ) : (
              <Icon
                color="status.danger"
                icon="x"
                tooltip={intl.formatMessage({
                  id: 'AlertsList.inactiveAlertTooltip',
                  defaultMessage: 'Inactive alert',
                  description: 'Tooltip for inactive alert icon',
                })}
              />
            )}
          </td>
          <td className={classes.cellMuted}>
            {muted ? (
              <Icon
                color="status.inactive"
                icon="alert-off"
                tooltip={intl.formatMessage({
                  id: 'AlertsList.mutedAlertTooltip',
                  defaultMessage: 'Muted alert',
                  description: 'Tooltip for muted alert icon',
                })}
              />
            ) : (
              <Icon
                color="background.fade"
                icon="alert"
                tooltip={intl.formatMessage({
                  id: 'AlertsList.unmutedAlertTooltip',
                  defaultMessage: 'Unmuted alert',
                  description: 'Tooltip for unmuted alert icon',
                })}
              />
            )}
          </td>
          <td className={classes.cellActions}>
            <ActionsMenu
              id={`alerts_row_actions_menu?id=${alert.id}`}
              items={this.getMenuItems(alert)}
              align="end"
              title={intl.formatMessage({
                id: 'AlertsList.editAlertMenuTitle',
                defaultMessage: 'edit alert menu',
                description: 'Title for alert actions menu',
              })}
            />
          </td>
        </tr>
      );
    });
  }

  renderAlertsTable() {
    const { classes, alerts, listError, handleRetry, searchQueries, intl } =
      this.props;
    const { allSelected, selectedItemsList } = this.state;

    if (listError) {
      return (
        <div className={classes.emptyContainer}>
          <div className={classes.primaryEmptyMessage}>
            {intl.formatMessage({
              id: 'AlertsList.loadingError',
              defaultMessage: 'There was an error loading alerts.',
              description: 'Error message when alerts fail to load',
            })}
          </div>
          <button className={classes.emptyMessageAction} onClick={handleRetry}>
            {intl.formatMessage({
              id: 'AlertsList.retryButton',
              defaultMessage: 'Retry',
              description: 'Button text to retry loading alerts',
            })}
          </button>
        </div>
      );
    }

    if (!alerts.length) {
      return (
        <div className={classes.emptyContainer}>
          {searchQueries && Object.keys(searchQueries).length ? (
            <Fragment>
              <div className={classes.primaryEmptyMessage}>
                {intl.formatMessage({
                  id: 'AlertsList.noMatchingAlerts',
                  defaultMessage: 'No alerts match the selected filters.',
                  description: 'Message when no alerts match the filters',
                })}
              </div>
            </Fragment>
          ) : (
            <EmptyPage preset="no-alerts" />
          )}
        </div>
      );
    }

    return (
      <table className={classes.alertsTable}>
        <thead>
          <tr>
            <th className={classes.headerCheckbox}>
              <div className={classes.firstCellInRowWrapper}>
                <div className={classes.checkboxWrapper}>
                  <EcCheckbox
                    id="select-all-alert-items"
                    checked={allSelected}
                    partiallyChecked={!!selectedItemsList.length}
                    onClick={() =>
                      this.handleOnSelectClick(!selectedItemsList.length)
                    }
                  />
                  <label
                    className="screenReaderText"
                    htmlFor="select-all-alert-items"
                  >
                    {intl.formatMessage({
                      id: 'AlertsList.selectAllLabel',
                      defaultMessage: 'Select all alert items',
                      description:
                        'Screen reader label for select all checkbox',
                    })}
                  </label>
                </div>
                <div className={classes.chevronIcon}>
                  <SelectDeselectAllMenu
                    name="alerts"
                    onChange={(selectAll) =>
                      this.handleOnSelectClick(selectAll)
                    }
                  />
                </div>
              </div>
            </th>
            {selectedItemsList.length ? (
              <th colSpan="8">
                <div className={classes.headerActions}>
                  <Button
                    onClick={() =>
                      this.setState({ primaryModal: { type: MODAL_DELETE } })
                    }
                    text={intl.formatMessage({
                      id: 'AlertsList.deleteButton',
                      defaultMessage: 'Delete',
                      description: 'Button text for delete action',
                    })}
                    variant="action"
                  />
                </div>
              </th>
            ) : (
              <Fragment>
                <th className={classes.headerName}>
                  {intl.formatMessage({
                    id: 'AlertsList.nameHeader',
                    defaultMessage: 'NAME',
                    description: 'Table header for alert name',
                  })}
                </th>
                <th className={classes.headerRecipients}>
                  {intl.formatMessage({
                    id: 'AlertsList.recipientsHeader',
                    defaultMessage: 'RECIPIENTS',
                    description: 'Table header for recipients',
                  })}
                </th>
                <th>
                  {intl.formatMessage({
                    id: 'AlertsList.createdByHeader',
                    defaultMessage: 'CREATED BY',
                    description: 'Table header for created by',
                  })}
                </th>
                <th>
                  {intl.formatMessage({
                    id: 'AlertsList.lastScheduledSendHeader',
                    defaultMessage: 'LAST SCHEDULED SEND',
                    description: 'Table header for last scheduled send',
                  })}
                </th>
                <th>
                  {intl.formatMessage({
                    id: 'AlertsList.nextScheduledSendHeader',
                    defaultMessage: 'NEXT SCHEDULED SEND',
                    description: 'Table header for next scheduled send',
                  })}
                </th>
                <th className={classes.headerActive}>
                  {intl.formatMessage({
                    id: 'AlertsList.activeHeader',
                    defaultMessage: 'ACTIVE',
                    description: 'Table header for active status',
                  })}
                </th>
                <th className={classes.headerMuted}>
                  {intl.formatMessage({
                    id: 'AlertsList.mutedHeader',
                    defaultMessage: 'MUTED',
                    description: 'Table header for muted status',
                  })}
                </th>
                <th className={classes.headerAlertActions} />
              </Fragment>
            )}
          </tr>
        </thead>
        <tbody>{this.renderAlertsItems()}</tbody>
      </table>
    );
  }

  renderLoadingContainer() {
    const { classes } = this.props;

    return (
      <div className={classes.loadingContainer}>
        <LoadingSpinner size="medium" />
      </div>
    );
  }

  renderPagination() {
    const { pageSize } = this.state;
    const { page, pageCount, handleOnPageChange } = this.props;

    if (pageCount <= pageSize) return null;

    return (
      <EcPaginate
        onPageChange={handleOnPageChange}
        pageCount={Math.ceil(pageCount / pageSize)}
        forcePage={page - 1}
      />
    );
  }

  render() {
    const { classes, listLoading } = this.props;
    const { primaryModal } = this.state;

    return (
      <div
        className={classes.alertsList}
        role="region"
        aria-labelledby="alerts-list"
      >
        <EcCard contentStyles={ecCardContentStyles}>
          {listLoading
            ? this.renderLoadingContainer()
            : this.renderAlertsTable()}
        </EcCard>
        <span className={classes.verticalSeparator} />
        {this.renderPagination()}
        {primaryModal &&
          primaryModal.type === MODAL_DELETE &&
          this.renderDeleteModal()}
        {primaryModal &&
          primaryModal.type === MODAL_SIMPLE_SEND_EMAIL &&
          this.renderSendEmailModal()}
        {primaryModal &&
          primaryModal.type === MODAL_SIMPLE_MUTE &&
          this.renderMuteAlertModal()}
        {primaryModal &&
          primaryModal.type === MODAL_SIMPLE_ACTIVATE &&
          this.renderActivateModal()}
        {primaryModal &&
          primaryModal.type === MODAL_ALERT &&
          this.renderAlertModal()}
      </div>
    );
  }
}

AlertsList.propTypes = {
  alerts: PropTypes.array.isRequired,
  listLoading: PropTypes.bool.isRequired,
  listError: PropTypes.bool.isRequired,
  page: PropTypes.number,
  pageCount: PropTypes.number,
  handleOnPageChange: PropTypes.func.isRequired,
  handleDeleteItems: PropTypes.func.isRequired,
  handleUpdateItem: PropTypes.func.isRequired,
  handleRetry: PropTypes.func.isRequired,
};

export default injectSheet(styles)(
  withFormatDate(withFlags(injectIntl(AlertsList))),
);
